import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import axios from 'axios'
import PedidoItsysPanel from '@/components/PedidoItsysPanel'
import {
  Package, Download, Loader2, Search, Filter, AlertTriangle,
  TrendingDown, Euro, Boxes, RefreshCw, BookmarkIcon, ShoppingCart, ExternalLink, Truck, Clock, CheckCircle, Store, PackageX
} from 'lucide-react'

// IDs de proveedores en Factusol
const PORTATILMOVIL_SUPPLIER_ID = '8'
const ITSYS_SUPPLIER_ID = '23'
const GREENCELL_SUPPLIER_ID = '2'
const CONTAINER_SUPPLIER_IDS = ['1']

interface StockNeedItem {
  reference: string
  supplier_reference: string | null
  description: string
  family_code: string
  family_name: string
  supplier_code: string | null
  supplier_name: string | null
  current_stock: number
  pending_to_receive: number
  expected_stock: number
  min_stock: number
  max_stock: number
  quantity_needed: number
  cost_price: number
  total_cost: number
  main_supplier_code?: string | null
  main_supplier_name?: string | null
  main_supplier_reference?: string | null
  supplier_stock?: number | null
  supplier_stock_note?: string | null
  supplier_switched?: boolean
  kind?: 'reponer' | 'bajo_pedido' | 'emergencia' | 'fabricar' | 'adelanto' | null
  components?: string | null
  backorder_units?: number
  sales_per_month?: number | null
  cover_weeks?: number | null
  level_reason?: string | null
  unregistered?: boolean
  forward_units?: number
  forward_tier?: number | null
  forward_reason?: string | null
  waiting_orders?: { id_order: number | null; order_reference?: string | null; date: string; units: number; customer?: string | null; origin?: string; manual_id?: number | null }[]
}

interface SupplierBasket {
  supplier_code: string
  supplier_name: string | null
  lines: number
  units: number
  cost: number
  backorders: number
  emergencies: number
  basket_threshold_eur: number
  review_days: number
  lead_days: number
  mode: string | null
  last_order_date: string | null
  days_since_last_order: number | null
  ready: boolean
  reasons: string[]
  forward_cost?: number
  need_cost?: number
  fill_to_threshold?: boolean
  out_of_stock_lines?: number
  out_of_stock_cost?: number
}

interface StockNeedsResponse {
  items: StockNeedItem[]
  total_products: number
  total_units_needed: number
  total_cost: number
  generated_at: string
  baskets?: SupplierBasket[]
  out_of_stock_items?: StockNeedItem[]  // el proveedor los tiene a 0: fuera del pedido y de los totales
  out_of_stock_cost?: number
}

// Quita los adelantos para el pedido mínimo (proveedores con fill_to_threshold): deja solo la necesidad real
const withoutForward = (d: StockNeedsResponse): StockNeedsResponse => {
  const items = d.items
    .map((i) => {
      if (!i.forward_units) return i
      const qty = i.quantity_needed - i.forward_units
      return { ...i, quantity_needed: qty, total_cost: Math.round(qty * i.cost_price * 100) / 100, forward_units: 0 }
    })
    .filter((i) => i.quantity_needed > 0)
  return {
    ...d,
    items,
    total_products: items.length,
    total_units_needed: items.reduce((a, i) => a + i.quantity_needed, 0),
    total_cost: items.reduce((a, i) => a + i.total_cost, 0),
  }
}

const FORWARD_TIER_LABEL: Record<number, string> = {
  1: 'el proveedor lo agota pronto',
  2: 'el proveedor lo agota a menudo',
  3: 'venta estable',
  4: 'alta rotación',
}

interface FilterOption {
  code: string
  name: string
}

interface FilterOptions {
  suppliers: FilterOption[]
  families: FilterOption[]
}

export default function ReportsSupplierOrdersPage() {
  // Filter states
  const [supplierFilter, setSupplierFilter] = useState<string>('')
  const [familyFilter, setFamilyFilter] = useState<string>('')
  const [searchFilter, setSearchFilter] = useState<string>('')
  const [minQuantityFilter, setMinQuantityFilter] = useState<string>('')
  // adelantos para llegar al pedido mínimo comprometido (se pueden quitar del listado y del CSV)
  const [includeForward, setIncludeForward] = useState(true)

  // Fetch filter options
  const filtersQuery = useQuery({
    queryKey: ['reports-supplier-orders-filters'],
    queryFn: async () => {
      const res = await axios.get<FilterOptions>('/api/reports/supplier-orders/filters')
      return res.data
    }
  })

  // Fetch stock needs data
  const stockNeedsQuery = useQuery({
    queryKey: ['reports-stock-needs', supplierFilter, familyFilter, searchFilter, minQuantityFilter],
    queryFn: async () => {
      const params: Record<string, string> = {}
      if (supplierFilter) params.supplier = supplierFilter
      if (familyFilter) params.family = familyFilter
      if (searchFilter) params.search = searchFilter
      if (minQuantityFilter) params.min_quantity = minQuantityFilter

      const res = await axios.get<StockNeedsResponse>('/api/reports/supplier-orders/stock-needs', { params })
      return res.data
    }
  })

  const handleExportCsv = () => {
    const params = new URLSearchParams()
    if (supplierFilter) params.append('supplier', supplierFilter)
    if (familyFilter) params.append('family', familyFilter)
    if (searchFilter) params.append('search', searchFilter)
    if (minQuantityFilter) params.append('min_quantity', minQuantityFilter)

    const url = `/api/reports/supplier-orders/stock-needs/export?${params.toString()}`
    window.open(url, '_blank')
  }

  const handleClearFilters = () => {
    setSupplierFilter('')
    setFamilyFilter('')
    setSearchFilter('')
    setMinQuantityFilter('')
  }

  // Estado para PortatilMovil
  const [portatilMovilLoading, setPortatilMovilLoading] = useState(false)
  const [portatilMovilModal, setPortatilMovilModal] = useState(false)

  // Estado para ITSys
  const [itsysCsvLoading, setItsysCsvLoading] = useState(false)

  // Estado para GreenCell
  const [greencellLoading, setGreencellLoading] = useState(false)

  // Contenedor: plantilla del proveedor (Screen REF / Surface / Comments)
  const [specsModal, setSpecsModal] = useState(false)
  const [specsText, setSpecsText] = useState('')
  const [specsResult, setSpecsResult] = useState<string | null>(null)
  const isContainer = CONTAINER_SUPPLIER_IDS.includes(supplierFilter)
  const handleDownloadContainerXlsx = () => {
    const params = new URLSearchParams({ supplier: supplierFilter })
    if (familyFilter) params.append('family', familyFilter)
    window.open(`/api/reports/supplier-orders/container/export-xlsx?${params.toString()}`, '_blank')
  }
  const handleSaveSpecs = async () => {
    try {
      const res = await axios.post('/api/replenishment/item-specs/template', { supplier_code: supplierFilter, text: specsText })
      setSpecsResult(`Guardadas ${res.data.saved} referencias`)
      setSpecsText('')
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } }
      setSpecsResult(err.response?.data?.detail || 'Error al guardar')
    }
  }

  // Preparar pedido para PortatilMovil
  const handlePreparePortatilMovil = async () => {
    if (!data || data.items.length === 0) return

    setPortatilMovilLoading(true)
    try {
      const itemsToSend = data.items
        .filter(item => item.supplier_reference && item.quantity_needed > 0)
        .map(item => ({
          supplier_reference: item.supplier_reference,
          quantity: Math.ceil(item.quantity_needed),
          description: item.description,
          jvs_reference: item.reference
        }))

      if (itemsToSend.length === 0) {
        alert('No hay productos con referencia de proveedor para enviar')
        return
      }

      const res = await axios.post<{
        resolved_product_ids?: number
        pm_online?: boolean
        ambiguous?: number
      }>('/api/reports/supplier-orders/portatilmovil/prepare', { items: itemsToSend })

      setPortatilMovilModal(true)
    } catch (error) {
      console.error('Error preparando pedido PortatilMovil:', error)
      const err = error as { response?: { data?: { detail?: string } }; message?: string }
      alert(`Error al preparar el pedido: ${err.response?.data?.detail || err.message || 'desconocido'}`)
    } finally {
      setPortatilMovilLoading(false)
    }
  }

  const handleOpenPortatilMovil = () => {
    window.open('https://www.portatilmovil.com', '_blank')
  }

  // Generar y descargar CSV para subir en la web de ITSys (formato sku,quantity)
  const handleDownloadItsysCsv = async () => {
    if (!data || data.items.length === 0) return

    setItsysCsvLoading(true)
    try {
      const itemsToSend = data.items
        .filter(item => item.supplier_reference && item.quantity_needed > 0)
        .map(item => ({
          supplier_reference: item.supplier_reference,
          quantity: Math.ceil(item.quantity_needed)
        }))

      if (itemsToSend.length === 0) {
        alert('No hay productos con referencia de proveedor para exportar')
        return
      }

      const response = await axios.post('/api/reports/supplier-orders/itsys/export-csv', {
        items: itemsToSend
      }, {
        responseType: 'blob'
      })

      const blob = new Blob([response.data], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `pedido_itsys_${new Date().toISOString().slice(0, 10)}.csv`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (error) {
      console.error('Error generando CSV ITSys:', error)
      alert('Error al generar el CSV. Inténtalo de nuevo.')
    } finally {
      setItsysCsvLoading(false)
    }
  }

  // Generar y descargar CSV para GreenCell
  const handleDownloadGreencellCsv = async () => {
    if (!data || data.items.length === 0) return

    setGreencellLoading(true)
    try {
      const itemsToSend = data.items
        .filter(item => item.supplier_reference && item.quantity_needed > 0)
        .map(item => ({
          supplier_reference: item.supplier_reference,
          quantity: Math.ceil(item.quantity_needed)
        }))

      if (itemsToSend.length === 0) {
        alert('No hay productos con referencia de proveedor para exportar')
        return
      }

      const response = await axios.post('/api/reports/supplier-orders/greencell/export-csv', {
        items: itemsToSend
      }, {
        responseType: 'blob'
      })

      const blob = new Blob([response.data], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `pedido_greencell_${new Date().toISOString().slice(0, 10)}.csv`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (error) {
      console.error('Error generando CSV GreenCell:', error)
      alert('Error al generar el CSV. Inténtalo de nuevo.')
    } finally {
      setGreencellLoading(false)
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2
    }).format(value)
  }

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('es-ES').format(value)
  }

  const rawData = stockNeedsQuery.data
  const hasForward = !!rawData?.items.some((i) => i.forward_units)
  const data = rawData && hasForward && !includeForward ? withoutForward(rawData) : rawData

  // Bookmarklet unificado
  const apiBase = window.location.origin + '/api'
  const unifiedBookmarkletUrl = `${apiBase}/reports/supplier-orders/unified-bookmarklet.js`
  const unifiedBookmarkletCode = `javascript:void(fetch('${unifiedBookmarkletUrl}?t='+Date.now()).then(r=>r.text()).then(eval))`

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="p-2 bg-indigo-100 dark:bg-indigo-900 rounded-lg">
            <Package className="w-8 h-8 text-indigo-600 dark:text-indigo-400" />
          </span>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
              Necesidades de Stock
            </h1>
            <p className="text-slate-500 dark:text-slate-400">
              Si stock + pendiente queda por debajo del mínimo, se pide hasta el máximo. Incluye vendidos sin stock y emergencias.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Botón PortatilMovil - solo visible cuando se filtra por proveedor 8 */}
          {supplierFilter === PORTATILMOVIL_SUPPLIER_ID && (
            <button
              onClick={handlePreparePortatilMovil}
              disabled={!data || data.items.length === 0 || portatilMovilLoading}
              className="flex items-center gap-2 px-4 py-2.5 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {portatilMovilLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <ShoppingCart className="w-4 h-4" />
              )}
              Preparar pedido PortatilMovil
            </button>
          )}

          {/* Botón CSV ITSys - fichero sku,quantity para subir en itsys.gr */}
          {supplierFilter === ITSYS_SUPPLIER_ID && (
            <button
              onClick={handleDownloadItsysCsv}
              disabled={!data || data.items.length === 0 || itsysCsvLoading}
              className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Descarga un CSV (sku,quantity) para subirlo en la web de ITSys"
            >
              {itsysCsvLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              Descargar CSV ITSys
            </button>
          )}

          {/* Contenedor: Excel en la plantilla del proveedor */}
          {isContainer && (
            <>
              <button
                onClick={handleDownloadContainerXlsx}
                disabled={!data || data.items.length === 0}
                className="flex items-center gap-2 px-4 py-2.5 bg-cyan-700 text-white rounded-xl font-medium hover:bg-cyan-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Excel con columnas JVS REF, Screen REF, Surface, QTY, Comments (cantidades redondeadas a cajas de 20)"
              >
                <Download className="w-4 h-4" />
                Excel contenedor
              </button>
              <button
                onClick={() => { setSpecsResult(null); setSpecsModal(true) }}
                className="flex items-center gap-2 px-4 py-2.5 bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-slate-100 rounded-xl font-medium hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors"
                title="Pegar la plantilla enviada al proveedor para guardar modelo de panel, acabado y comentarios por referencia"
              >
                Plantilla proveedor
              </button>
            </>
          )}

          {/* Botón GreenCell - solo visible cuando se filtra por proveedor 2 */}
          {supplierFilter === GREENCELL_SUPPLIER_ID && (
            <button
              onClick={handleDownloadGreencellCsv}
              disabled={!data || data.items.length === 0 || greencellLoading}
              className="flex items-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {greencellLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              Descargar CSV GreenCell
            </button>
          )}
        </div>
      </div>

      {/* Pedido automático a ITSYS (extensión de Chrome; modo prueba: prepara el carrito, no confirma) */}
      {supplierFilter === ITSYS_SUPPLIER_ID && data && (
        <PedidoItsysPanel items={data.items} />
      )}

      {/* Bookmarklet unificado (ITSYS se pide con la extensión de Chrome) */}
      {supplierFilter !== ITSYS_SUPPLIER_ID && <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
        <div className="px-5 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <BookmarkIcon className="w-5 h-5 text-slate-400" />
              <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Bookmarklet de proveedor</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                  Arrastra el boton a tu barra de marcadores. Detecta automaticamente el proveedor.
                </p>
                <div className="flex items-center gap-2 mt-1.5">
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300">portatilmovil.com</span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">itsys.gr</span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">greencell.global</span>
                </div>
              </div>
            </div>
            <a
              href={unifiedBookmarkletCode}
              onClick={(e) => e.preventDefault()}
              draggable
              className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 via-blue-600 to-green-600 text-white text-sm font-semibold rounded-lg hover:from-purple-700 hover:via-blue-700 hover:to-green-700 transition-all cursor-grab active:cursor-grabbing shadow-sm shrink-0"
            >
              <BookmarkIcon className="w-4 h-4" />
              JVS Proveedor
            </a>
          </div>
          <div className="mt-3 text-xs text-slate-400 dark:text-slate-500 flex items-center gap-4">
            <span><b>1.</b> Ir a la web del proveedor</span>
            <span>&rarr;</span>
            <span><b>2.</b> Abrir pedido o carrito</span>
            <span>&rarr;</span>
            <span><b>3.</b> Click en el marcador &rarr; elegir accion</span>
          </div>
        </div>
      </div>}

      {/* Estado de cestas por proveedor */}
      {rawData?.baskets && rawData.baskets.length > 0 && (
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-5">
          <div className="flex items-center gap-2 mb-3">
            <Truck className="w-5 h-5 text-slate-400" />
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Cestas por proveedor</h2>
            <span className="text-xs text-slate-400">lista = umbral de portes alcanzado o cadencia cumplida</span>
            {hasForward && (
              <label className="ml-auto inline-flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 cursor-pointer" title="Unidades extra para llegar al pedido mínimo comprometido con el proveedor. Se incluyen en el listado y en el CSV.">
                <input type="checkbox" checked={includeForward} onChange={(e) => setIncludeForward(e.target.checked)} className="rounded border-slate-300 text-sky-600 focus:ring-sky-500" />
                Incluir adelantos
              </label>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {rawData.baskets.map((b) => (
              <button
                key={b.supplier_code}
                onClick={() => setSupplierFilter(b.supplier_code)}
                className={`text-left p-4 rounded-xl border transition-colors ${b.ready
                  ? 'border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 dark:border-emerald-800'
                  : 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40'} ${supplierFilter === b.supplier_code ? 'ring-2 ring-indigo-500' : ''}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-slate-900 dark:text-white">{b.supplier_name || `Proveedor ${b.supplier_code}`}</span>
                  {b.ready ? (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 dark:text-emerald-300"><CheckCircle className="w-3.5 h-3.5" /> lista</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs text-slate-400"><Clock className="w-3.5 h-3.5" /> acumulando</span>
                  )}
                </div>
                <p className="text-sm text-slate-700 dark:text-slate-300 mt-1">
                  {b.lines} líneas · {formatNumber(b.units)} uds · <b>{formatCurrency(b.cost)}</b>
                  {b.basket_threshold_eur > 0 && <span className="text-slate-400"> / {formatCurrency(b.basket_threshold_eur)}</span>}
                </p>
                {b.fill_to_threshold && (
                  <p className="text-xs text-slate-600 dark:text-slate-300 mt-1">
                    Pedido mínimo {formatCurrency(b.basket_threshold_eur)}: necesidad {formatCurrency(b.need_cost || 0)}
                    {(b.forward_cost || 0) > 0 && <> + <span className="text-sky-700 dark:text-sky-300 font-medium">adelantos {formatCurrency(b.forward_cost || 0)}</span>{!includeForward && ' (ocultos)'}</>}
                    {b.cost < b.basket_threshold_eur && <span className="text-amber-700 dark:text-amber-300"> · faltan {formatCurrency(b.basket_threshold_eur - b.cost)} sin candidatos</span>}
                  </p>
                )}
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  Último pedido: {b.last_order_date ? `${b.last_order_date} (hace ${b.days_since_last_order} d)` : 'sin registro'} · cadencia {b.review_days} d · plazo {b.lead_days} d
                </p>
                {(b.backorders > 0 || b.emergencies > 0) && (
                  <p className="text-xs mt-1">
                    {b.backorders > 0 && <span className="inline-block mr-2 px-1.5 py-0.5 rounded bg-danger-100 text-danger-700 dark:bg-danger-900/40 dark:text-danger-300">{b.backorders} vendidos sin stock</span>}
                    {b.emergencies > 0 && <span className="inline-block px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">{b.emergencies} emergencias</span>}
                  </p>
                )}
                {b.reasons.length > 0 && <p className="text-xs text-emerald-700 dark:text-emerald-300 mt-1">{b.reasons.join(' · ')}</p>}
                {(b.out_of_stock_lines || 0) > 0 && (
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    Fuera del pedido: {b.out_of_stock_lines} sin stock en el proveedor ({formatCurrency(b.out_of_stock_cost || 0)})
                  </p>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-5">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-orange-600 dark:text-orange-400" />
            </div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">Productos a reponer</p>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">
                {stockNeedsQuery.isLoading ? (
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                ) : (
                  formatNumber(data?.total_products || 0)
                )}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-5">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <Boxes className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">Unidades necesarias</p>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">
                {stockNeedsQuery.isLoading ? (
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                ) : (
                  formatNumber(data?.total_units_needed || 0)
                )}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-5">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
              <Euro className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">Coste estimado</p>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">
                {stockNeedsQuery.isLoading ? (
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                ) : (
                  formatCurrency(data?.total_cost || 0)
                )}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-5">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-slate-400" />
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Filtros</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Supplier Filter */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
              Proveedor
            </label>
            <select
              value={supplierFilter}
              onChange={(e) => setSupplierFilter(e.target.value)}
              className="w-full px-3 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">Todos los proveedores</option>
              {filtersQuery.data?.suppliers.map((s) => (
                <option key={s.code} value={s.code}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>

          {/* Family Filter */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
              Familia
            </label>
            <select
              value={familyFilter}
              onChange={(e) => setFamilyFilter(e.target.value)}
              className="w-full px-3 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">Todas las familias</option>
              {filtersQuery.data?.families.map((f) => (
                <option key={f.code} value={f.code}>
                  {f.code} - {f.name}
                </option>
              ))}
            </select>
          </div>

          {/* Search Filter */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
              Buscar referencia
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                placeholder="Ej: BAT-AS08"
                className="w-full pl-10 pr-3 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>

          {/* Min Quantity Filter */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
              Cantidad minima
            </label>
            <div className="relative">
              <TrendingDown className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="number"
                value={minQuantityFilter}
                onChange={(e) => setMinQuantityFilter(e.target.value)}
                placeholder="Min. uds"
                min="0"
                className="w-full pl-10 pr-3 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
          <button
            onClick={handleClearFilters}
            className="text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors"
          >
            Limpiar filtros
          </button>
          <button
            onClick={() => stockNeedsQuery.refetch()}
            disabled={stockNeedsQuery.isFetching}
            className="flex items-center gap-2 text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${stockNeedsQuery.isFetching ? 'animate-spin' : ''}`} />
            Actualizar
          </button>
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                  Ref. JVS
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                  Ref. Proveedor
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                  Descripcion
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                  Familia
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider" title="Quién genera la demanda: PrestaShop (pedidos rosas / contrarreembolso) o el usuario que añadió la pieza pendiente">
                  Origen
                </th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                  Stock Actual
                </th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                  Pend. Recibir
                </th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                  Mín / Máx
                </th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider" title="Ritmo real de venta (uds/mes) y semanas de cobertura del stock actual">
                  Venta/mes
                </th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider" title="Stock en el catálogo del proveedor propuesto">
                  Stock prov.
                </th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider bg-orange-50 dark:bg-orange-900/20">
                  Necesario
                </th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                  P. Coste
                </th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                  Total
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
              {stockNeedsQuery.isLoading ? (
                <tr>
                  <td colSpan={13} className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
                      <p className="text-slate-500 dark:text-slate-400">Cargando datos...</p>
                    </div>
                  </td>
                </tr>
              ) : stockNeedsQuery.isError ? (
                <tr>
                  <td colSpan={13} className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <AlertTriangle className="w-8 h-8 text-danger-500" />
                      <p className="text-danger-600 dark:text-danger-400">Error al cargar los datos</p>
                    </div>
                  </td>
                </tr>
              ) : data?.items.length === 0 ? (
                <tr>
                  <td colSpan={13} className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <Boxes className="w-8 h-8 text-slate-400" />
                      <p className="text-slate-500 dark:text-slate-400">
                        No hay productos que necesiten reposicion
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                data?.items.map((item) => (
                  <tr
                    key={`${item.reference || item.supplier_reference}-${item.kind || 'r'}-${item.supplier_code || ''}`}
                    className={`hover:bg-slate-50 dark:hover:bg-slate-900/50 transition-colors ${item.kind === 'bajo_pedido' ? 'bg-danger-50/40 dark:bg-danger-900/10' : item.kind === 'emergencia' ? 'bg-amber-50/40 dark:bg-amber-900/10' : item.kind === 'fabricar' ? 'bg-violet-50/40 dark:bg-violet-900/10' : item.kind === 'adelanto' ? 'bg-sky-50/40 dark:bg-sky-900/10' : ''}`}
                  >
                    <td className="px-4 py-3">
                      {item.unregistered ? (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300" title="Producto sin alta en Factusol: se pide por la referencia del proveedor">
                          sin alta
                        </span>
                      ) : (
                        <span className="font-mono text-sm font-medium text-indigo-600 dark:text-indigo-400">
                          {item.reference}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-sm text-slate-600 dark:text-slate-400">
                        {item.supplier_reference || '-'}
                      </span>
                      <div className="flex flex-wrap gap-1 mt-0.5">
                        {item.supplier_switched && item.kind !== 'emergencia' && (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300" title={`El habitual (${item.main_supplier_name || item.main_supplier_code}) no tiene stock`}>
                            alt: {item.supplier_name}
                          </span>
                        )}
                        {item.kind === 'emergencia' && (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" title="Se agota antes de que llegue el siguiente contenedor">
                            emergencia
                          </span>
                        )}
                        {item.kind === 'fabricar' && (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300" title={item.components || ''}>
                            fabricar: {item.components}
                          </span>
                        )}
                        {(item.forward_units || 0) > 0 && (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300" title={item.forward_reason || ''}>
                            {item.kind === 'adelanto' ? 'adelanto' : `+${item.forward_units} adelanto`}: {FORWARD_TIER_LABEL[item.forward_tier || 0] || 'pedido mínimo'}
                          </span>
                        )}
                        {item.kind === 'bajo_pedido' && (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-danger-100 text-danger-700 dark:bg-danger-900/40 dark:text-danger-300" title="Vendido sin stock; artículo bajo pedido">
                            vendido sin stock
                          </span>
                        )}
                        {item.kind === 'reponer' && (item.backorder_units || 0) > 0 && (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-danger-100 text-danger-700 dark:bg-danger-900/40 dark:text-danger-300">
                            {item.backorder_units} vendidos sin stock
                          </span>
                        )}
                        {(item.waiting_orders || []).map((o) => (
                          <span
                            key={o.manual_id ? `m${o.manual_id}` : `o${o.id_order}`}
                            className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${o.manual_id
                              ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300'
                              : 'bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300'}`}
                            title={`${o.manual_id ? `Pieza pendiente añadida por ${o.origin}` : 'Pedido pendiente por falta de stock'} · ${o.customer || ''} · ${o.units} ud${o.units === 1 ? '' : 's'}`}
                          >
                            {o.id_order ? `espera pedido ${o.id_order}` : 'pieza pendiente'} ({o.date.slice(8, 10)}/{o.date.slice(5, 7)})
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-slate-900 dark:text-white line-clamp-1" title={item.description}>
                        {item.description}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-slate-600 dark:text-slate-400">
                        {item.family_name}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col items-start gap-1">
                        {Array.from(new Set((item.waiting_orders || []).map((o) => o.origin || 'PrestaShop'))).map((og) => (
                          og === 'PrestaShop' ? (
                            <span key={og} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300">
                              <Store className="w-3 h-3" /> PrestaShop
                            </span>
                          ) : (
                            <span key={og} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">
                              {og}
                            </span>
                          )
                        ))}
                        {(item.waiting_orders || []).length === 0 && (
                          <span className="text-xs text-slate-400" title={item.kind === 'adelanto' ? (item.forward_reason || '') : 'Reposición por stock mínimo/máximo'}>{item.kind === 'adelanto' ? 'pedido mínimo' : 'reposición'}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={`text-sm font-medium ${
                        item.current_stock <= 0
                          ? 'text-danger-600 dark:text-danger-400'
                          : 'text-slate-900 dark:text-white'
                      }`}>
                        {item.current_stock}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={`text-sm ${
                        item.pending_to_receive > 0
                          ? 'text-blue-600 dark:text-blue-400 font-medium'
                          : 'text-slate-400'
                      }`}>
                        {item.pending_to_receive > 0 ? `+${item.pending_to_receive}` : '0'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right" title={item.level_reason || ''}>
                      <span className="text-sm text-slate-600 dark:text-slate-400">
                        {item.min_stock} / {item.max_stock > 0 ? item.max_stock : '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-sm text-slate-600 dark:text-slate-400">
                        {item.sales_per_month != null ? item.sales_per_month.toFixed(1) : '—'}
                      </span>
                      {item.cover_weeks != null && (
                        <span className="block text-[10px] text-slate-400">{item.cover_weeks} sem</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={`text-sm ${item.supplier_stock === 0 ? 'text-danger-600 dark:text-danger-400 font-medium' : 'text-slate-600 dark:text-slate-400'}`} title={item.supplier_stock_note ?? undefined}>
                        {item.supplier_stock_note ? 'disp.' : (item.supplier_stock == null ? '—' : item.supplier_stock)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right bg-orange-50 dark:bg-orange-900/20">
                      <span className="text-sm font-bold text-orange-600 dark:text-orange-400">
                        {item.quantity_needed}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-sm text-slate-600 dark:text-slate-400">
                        {formatCurrency(item.cost_price)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-sm font-medium text-slate-900 dark:text-white">
                        {formatCurrency(item.total_cost)}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer with generation time */}
        {data && (
          <div className="px-4 py-3 bg-slate-50 dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700">
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Informe generado: {new Date(data.generated_at).toLocaleString('es-ES')}
              {' '} | {data.total_products} productos | {formatNumber(data.total_units_needed)} unidades | {formatCurrency(data.total_cost)}
            </p>
          </div>
        )}
      </div>

      {/* Sin stock en el proveedor: no se pueden pedir, no cuentan en el pedido ni en los totales */}
      {rawData?.out_of_stock_items && rawData.out_of_stock_items.length > 0 && (
        <details className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
          <summary className="px-5 py-4 cursor-pointer flex flex-wrap items-center gap-2 text-slate-900 dark:text-white">
            <PackageX className="w-5 h-5 text-slate-400" />
            <span className="font-semibold">Sin stock en el proveedor</span>
            <span className="text-sm text-slate-500 dark:text-slate-400">
              {rawData.out_of_stock_items.length} líneas · {formatNumber(rawData.out_of_stock_items.reduce((a, i) => a + i.quantity_needed, 0))} uds · {formatCurrency(rawData.out_of_stock_cost || 0)} — no entran en el pedido; vuelven solas cuando el proveedor tenga stock
            </span>
          </summary>
          <div className="overflow-x-auto border-t border-slate-200 dark:border-slate-700">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-900 text-left text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400">
                <tr>
                  <th className="px-4 py-2">Ref. JVS</th>
                  <th className="px-4 py-2">Ref. proveedor</th>
                  <th className="px-4 py-2">Descripción</th>
                  <th className="px-4 py-2">Proveedor</th>
                  <th className="px-4 py-2 text-right">Stock</th>
                  <th className="px-4 py-2 text-right">Necesario</th>
                  <th className="px-4 py-2 text-right">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                {rawData.out_of_stock_items.map((item, idx) => (
                  <tr key={`${item.reference}-${item.kind}-${idx}`} className="text-slate-700 dark:text-slate-300">
                    <td className="px-4 py-2 font-semibold text-slate-900 dark:text-white whitespace-nowrap">
                      {item.reference}
                      {(item.kind === 'bajo_pedido' || (item.backorder_units || 0) > 0) && (
                        <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-medium bg-danger-100 text-danger-700 dark:bg-danger-900/40 dark:text-danger-300">vendido</span>
                      )}
                    </td>
                    <td className="px-4 py-2 font-mono whitespace-nowrap">{item.supplier_reference || '—'}</td>
                    <td className="px-4 py-2 max-w-md truncate" title={item.description}>{item.description}</td>
                    <td className="px-4 py-2 whitespace-nowrap">{item.supplier_name || item.supplier_code}</td>
                    <td className="px-4 py-2 text-right">{item.current_stock}</td>
                    <td className="px-4 py-2 text-right">{item.quantity_needed}</td>
                    <td className="px-4 py-2 text-right">{formatCurrency(item.total_cost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}

      {/* Modal plantilla contenedor */}
      {specsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl max-w-2xl w-full mx-4 overflow-hidden">
            <div className="p-6 border-b border-slate-200 dark:border-slate-700">
              <h3 className="text-xl font-bold text-slate-900 dark:text-white">Plantilla del proveedor de contenedor</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                Pega las líneas del Excel (JVS REF, Screen REF, Surface, QTY, Comments). Se guardan el modelo de panel, el acabado y los comentarios de cada referencia; la cantidad se ignora.
              </p>
            </div>
            <div className="p-6">
              <textarea
                value={specsText}
                onChange={(e) => setSpecsText(e.target.value)}
                rows={12}
                placeholder={'PANT-062\tB156HAN02.0\tmatte\t400\tips without brackets'}
                className="w-full font-mono text-xs px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
              />
              {specsResult && <p className="text-sm mt-2 text-slate-700 dark:text-slate-300">{specsResult}</p>}
            </div>
            <div className="p-6 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-3">
              <button onClick={() => setSpecsModal(false)} className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white">Cerrar</button>
              <button onClick={handleSaveSpecs} disabled={!specsText.trim()} className="px-4 py-2.5 bg-cyan-700 text-white rounded-xl font-medium hover:bg-cyan-800 disabled:opacity-50">Guardar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal PortatilMovil */}
      {portatilMovilModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl max-w-lg w-full mx-4 overflow-hidden">
            <div className="p-6 border-b border-slate-200 dark:border-slate-700">
              <h3 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <ShoppingCart className="w-6 h-6 text-purple-600" />
                Pedido preparado para PortatilMovil
              </h3>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg p-4">
                <p className="text-green-800 dark:text-green-200 font-medium">
                  Se han preparado {data?.items.filter(i => i.supplier_reference && i.quantity_needed > 0).length} productos
                </p>
              </div>

              <div className="space-y-3">
                <h4 className="font-semibold text-slate-900 dark:text-white">Instrucciones:</h4>
                <ol className="list-decimal list-inside space-y-2 text-sm text-slate-600 dark:text-slate-300">
                  <li>Haz clic en "Abrir PortatilMovil" para ir a la web</li>
                  <li>Inicia sesion en tu cuenta de PortatilMovil</li>
                  <li>Haz clic en el favorito/bookmarklet "JVS Refs PM"</li>
                  <li>Espera a que se añadan todos los productos al carrito</li>
                </ol>
              </div>
            </div>

            <div className="p-6 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-3">
              <button
                onClick={() => setPortatilMovilModal(false)}
                className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white transition-colors"
              >
                Cerrar
              </button>
              <button
                onClick={() => {
                  handleOpenPortatilMovil()
                  setPortatilMovilModal(false)
                }}
                className="flex items-center gap-2 px-4 py-2.5 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                Abrir PortatilMovil
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}
